import React, { memo, useCallback, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FaComments,
  FaBrain,
  FaPaperPlane,
  FaUsers,
  FaDatabase,
  FaChartLine,
} from "react-icons/fa";
import { WORKFLOW_STEPS } from "./data";

const ICONS = {
  comment: FaComments,
  brain: FaBrain,
  send: FaPaperPlane,
  users: FaUsers,
  database: FaDatabase,
  trending: FaChartLine,
};

// Same product-tour feel as Insta Tools AutomationShowcaseMockup
const DEMO_TIMING = {
  pauseTopMs: 900,
  scrollDownMs: 4200,
  holdBottomMs: 1600,
  scrollUpMs: 900,
  holdAfterResetMs: 700,
};

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

const WorkflowAnimation = ({ active }) => {
  const bodyRef = useRef(null);
  const rafRef = useRef(null);
  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timersRef.current.push(id);
  }, []);

  // Auto-scroll loop so steps after "Auto DM Sent" come into view
  useEffect(() => {
    clearTimers();
    const el = bodyRef.current;
    if (!el) return undefined;

    el.scrollTop = 0;

    if (!active) return undefined;

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion) {
      // Jump once to bottom so later steps remain reachable without animation
      schedule(() => {
        if (bodyRef.current) {
          bodyRef.current.scrollTop = Math.max(
            0,
            bodyRef.current.scrollHeight - bodyRef.current.clientHeight
          );
        }
      }, 400);
      return clearTimers;
    }

    let cancelled = false;

    const animateScroll = (from, to, durationMs, onDone) => {
      const start = performance.now();
      const tick = (now) => {
        if (cancelled || !bodyRef.current) return;
        const progress = Math.min(1, (now - start) / durationMs);
        bodyRef.current.scrollTop = from + (to - from) * easeInOut(progress);
        if (progress < 1) {
          rafRef.current = window.requestAnimationFrame(tick);
        } else if (onDone) {
          onDone();
        }
      };
      rafRef.current = window.requestAnimationFrame(tick);
    };

    const runLoop = () => {
      if (cancelled || !bodyRef.current) return;
      const node = bodyRef.current;
      node.scrollTop = 0;
      const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);

      // If everything fits, no need to scroll — still keep loop alive for resize
      if (maxScroll < 8) {
        schedule(runLoop, DEMO_TIMING.pauseTopMs + DEMO_TIMING.holdBottomMs);
        return;
      }

      schedule(() => {
        if (cancelled) return;
        animateScroll(0, maxScroll, DEMO_TIMING.scrollDownMs, () => {
          schedule(() => {
            if (cancelled || !bodyRef.current) return;
            animateScroll(maxScroll, 0, DEMO_TIMING.scrollUpMs, () => {
              schedule(runLoop, DEMO_TIMING.holdAfterResetMs);
            });
          }, DEMO_TIMING.holdBottomMs);
        });
      }, DEMO_TIMING.pauseTopMs);
    };

    // Wait for step entrance animations before measuring scroll height
    schedule(runLoop, 700);

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [active, clearTimers, schedule]);

  // Prefer auto-demo scroll over manual wheel/touch (Insta Tools pattern)
  useEffect(() => {
    const node = bodyRef.current;
    if (!node || !active) return undefined;
    const block = (e) => e.preventDefault();
    node.addEventListener("wheel", block, { passive: false });
    node.addEventListener("touchmove", block, { passive: false });
    return () => {
      node.removeEventListener("wheel", block);
      node.removeEventListener("touchmove", block);
    };
  }, [active]);

  return (
    <div className="mx-auto flex h-full w-full max-w-md flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0b0b0f]/90 shadow-2xl backdrop-blur">
      <div className="shrink-0 border-b border-white/10 px-3.5 py-2.5 sm:px-5 sm:py-3">
        <p className="text-[10px] uppercase tracking-wider text-white/40 sm:text-xs">
          Automated brand pipeline
        </p>
        <p className="mt-0.5 text-[10px] text-white/50 sm:text-xs">
          Keyword → follow gate → DM → conversion
        </p>
      </div>

      <div
        ref={bodyRef}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3.5 py-3 sm:px-5 sm:py-4 [scrollbar-width:none] [-ms-overflow-style:none]"
      >
        <div className="relative flex flex-col gap-0.5 pb-2">
          {WORKFLOW_STEPS.map((step, i) => {
            const Icon = ICONS[step.icon];
            const isLast = i === WORKFLOW_STEPS.length - 1;
            return (
              <div key={step.id}>
                <motion.div
                  initial={{ opacity: 0, x: -16 }}
                  animate={active ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
                  transition={{ delay: i * 0.2, type: "spring", stiffness: 260, damping: 24 }}
                  className="flex items-center gap-2.5 sm:gap-3"
                >
                  <motion.div
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border sm:h-10 sm:w-10 ${
                      i % 2 === 0
                        ? "border-orange-500/30 bg-orange-500/10 text-orange-400"
                        : "border-blue-500/30 bg-blue-500/10 text-blue-400"
                    }`}
                    animate={active ? { y: [0, -3, 0] } : {}}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.25,
                    }}
                  >
                    <Icon className="text-sm" />
                  </motion.div>
                  <div className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/5 px-2.5 py-1.5 sm:px-3 sm:py-2">
                    <span className="text-[10px] text-white/40">Step {i + 1}</span>
                    <p className="text-xs font-medium text-white sm:text-sm">{step.label}</p>
                  </div>
                </motion.div>

                {!isLast && (
                  <div className="ml-4 flex h-3.5 items-center sm:ml-5 sm:h-4">
                    <motion.div
                      className="w-px origin-top bg-gradient-to-b from-orange-500/60 to-blue-500/60"
                      initial={{ scaleY: 0 }}
                      animate={active ? { scaleY: 1 } : { scaleY: 0 }}
                      transition={{ delay: i * 0.2 + 0.12, duration: 0.3 }}
                      style={{ height: "100%" }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(WorkflowAnimation);
